import React from 'react';

/**
 * Turns an audit row into a one-line human sentence suitable for the list
 * view's primary column. The raw action code remains visible (in mono, as
 * a secondary line) so this is a *replacement for scanning*, not a
 * replacement for the authoritative identifier.
 *
 * Each case is a deliberate template. When we add new audit actions on the
 * backend, add the matching template here — a missing case falls back to
 * a generic "<action> on <target>" which is still readable, just less
 * specific.
 *
 * Keep this file free of business logic. It only formats what the row
 * already carries in its metadata/target fields.
 */

export interface AuditRowLike {
    action: string;
    actor_email: string | null;
    actor_name: string | null;
    target_type: string | null;
    target_id: number | null;
    target_company_id: number | null;
    target_company_name: string | null;
    target_user_email?: string | null;
    target_user_username?: string | null;
    target_user_fullname?: string | null;
    metadata?: Record<string, any> | null;
}

function userLabel(row: AuditRowLike): string {
    // Order: fullname → username → email → bare id. Strongest identity first.
    if (row.target_user_fullname) return row.target_user_fullname;
    if (row.target_user_username) return `@${row.target_user_username}`;
    if (row.target_user_email) return row.target_user_email;
    if (row.metadata?.target_user_fullname) return row.metadata.target_user_fullname;
    if (row.metadata?.target_user_email) return row.metadata.target_user_email;
    return `user #${row.target_id ?? '?'}`;
}

function companyLabel(row: AuditRowLike): string {
    if (row.target_company_name) return row.target_company_name;
    if (row.metadata?.target_company_name) return row.metadata.target_company_name;
    return `company #${row.target_company_id ?? '?'}`;
}

function actorLabel(row: AuditRowLike): string {
    // Prefer the super-admin's full name over the email snapshot — names are
    // what humans scan for. Fall back to email, then to 'system' for
    // cron/background rows (super_admin_id is null).
    if (row.actor_name) return row.actor_name;
    if (row.actor_email) return row.actor_email;
    return 'system';
}

function strong(s: string, color?: string): React.ReactNode {
    return <strong style={{ color: color ?? 'var(--sa-text)' }}>{s}</strong>;
}

/**
 * Returns a React node — not a plain string — so we can bold/color key
 * pieces (company names, target users) without HTML-injection from the
 * data layer.
 */
export function describeAction(row: AuditRowLike): React.ReactNode {
    const a = row.action;
    const m = row.metadata ?? {};

    // ── Logins ──────────────────────────────────────────────────────────
    if (a === 'sa.login.success') {
        return (
            <>
                {strong(actorLabel(row))} logged in{m.new_ip ? ' from a new IP' : ''}
            </>
        );
    }
    if (a === 'sa.login.failed') {
        return <>Failed login attempt for {strong(m.attempted_email ?? actorLabel(row))}</>;
    }
    if (a === 'sa.login.new_ip_alert_sent') {
        const recipients = Array.isArray(m.recipients) ? m.recipients.length : '?';
        return <>New-IP alert emailed to {strong(String(recipients))} peer{recipients === 1 ? '' : 's'}</>;
    }
    if (a === 'sa.logout') {
        return <>{strong(actorLabel(row))} logged out</>;
    }

    // ── TOTP ────────────────────────────────────────────────────────────
    if (a === 'sa.totp.enrolled') {
        return <>{strong(actorLabel(row))} enrolled TOTP</>;
    }
    if (a === 'sa.totp.recovery_code_used') {
        return <>{strong(actorLabel(row))} used a recovery code (index {m.code_index ?? '?'})</>;
    }
    if (a === 'sa.totp.reset_cli') {
        return <>TOTP reset via CLI for {strong(m.target_email ?? '?')}</>;
    }
    if (a === 'sa.totp.reset_approved') {
        return <>{strong(actorLabel(row))} approved TOTP reset for {strong(m.target_email ?? '?')}</>;
    }

    // ── Company lifecycle ───────────────────────────────────────────────
    if (a === 'sa.company.created') {
        return (
            <>
                {strong(actorLabel(row))} created tenant {strong(companyLabel(row), 'var(--sa-accent)')}
                {m.initial_admin_email && <> with admin {m.initial_admin_email}</>}
            </>
        );
    }
    if (a === 'sa.company.updated') {
        const changed = Array.isArray(m.changed_fields) ? m.changed_fields.join(', ') : null;
        return (
            <>
                {strong(actorLabel(row))} updated {strong(companyLabel(row))}
                {changed && <> — changed: <span className="sa-mono" style={{ fontSize: 'var(--sa-fs-xs)' }}>{changed}</span></>}
            </>
        );
    }
    if (a === 'sa.company.suspended') {
        return (
            <>
                {strong(actorLabel(row))} suspended {strong(companyLabel(row), 'var(--sa-danger)')}
                {m.reason && <> — "{String(m.reason).slice(0, 80)}"</>}
            </>
        );
    }
    if (a === 'sa.company.marked_inactive') {
        return (
            <>
                {strong(actorLabel(row))} marked {strong(companyLabel(row), 'var(--sa-danger)')} as inactive
                {m.reason && <> — "{String(m.reason).slice(0, 80)}"</>}
            </>
        );
    }
    if (a === 'sa.company.reactivated') {
        return (
            <>
                {strong(actorLabel(row))} reactivated {strong(companyLabel(row), 'var(--sa-success)')}
                {m.from_status && <> (was {m.from_status})</>}
            </>
        );
    }
    if (a === 'sa.company.archive_reminders_dispatched') {
        return (
            <>
                Archive digest sent — {strong(String(m.company_count ?? '?'))} tenant(s) to {strong(String(m.recipients_count ?? '?'))} super-admin(s)
            </>
        );
    }

    // ── Users ───────────────────────────────────────────────────────────
    if (a === 'sa.user.created') {
        return (
            <>
                {strong(actorLabel(row))} created {m.initial_admin ? 'initial admin' : 'user'} {strong(userLabel(row))} in {companyLabel(row)}
            </>
        );
    }
    if (a === 'sa.user.password_reset') {
        return <>{strong(actorLabel(row))} reset password for {strong(userLabel(row))}</>;
    }

    // ── Impersonation ───────────────────────────────────────────────────
    if (a === 'sa.impersonation.started') {
        return (
            <>
                {strong(actorLabel(row))} started {m.duration_minutes ? `${m.duration_minutes}-min ` : ''}impersonation of {strong(userLabel(row), 'var(--sa-accent)')} @ {strong(companyLabel(row))}
            </>
        );
    }
    if (a === 'sa.impersonation.exchanged') {
        return (
            <>
                Impersonation token exchanged for {strong(userLabel(row), 'var(--sa-accent)')} @ {companyLabel(row)}
            </>
        );
    }
    if (a === 'sa.impersonation.ended') {
        const reasonMap: Record<string, string> = {
            ttl: 'auto — TTL expired',
            user: 'ended by god-user',
            force: 'force-ended by peer',
            write_blocked: 'ended after blocked write',
        };
        const r = reasonMap[m.reason] ?? m.reason ?? 'ended';
        const writes = m.write_attempt_count ?? 0;
        return (
            <>
                Impersonation of {strong(userLabel(row))} ended — {r}
                {writes > 0 && <> · <span style={{ color: 'var(--sa-danger)' }}>{writes} write attempt(s)</span></>}
            </>
        );
    }
    if (a === 'sa.impersonation.write_blocked') {
        return (
            <>
                <span style={{ color: 'var(--sa-danger)', fontWeight: 600 }}>BLOCKED WRITE</span>:{' '}
                <span className="sa-mono" style={{ fontSize: 'var(--sa-fs-xs)' }}>
                    {m.method ?? '?'} /{m.route ?? '?'}
                </span>
                {' '}during impersonation of {strong(userLabel(row))}
            </>
        );
    }
    if (a === 'sa.impersonation.force_ended') {
        return <>{strong(actorLabel(row))} force-ended impersonation session #{row.metadata?.session_id ?? '?'}</>;
    }

    // ── Audit viewer meta ───────────────────────────────────────────────
    if (a === 'sa.audit.viewed') {
        // Row count is always ≈page size (25) so dropping it cuts noise —
        // the interesting bit is "who looked and when", not how many rows
        // happened to be on that page.
        return <>{strong(actorLabel(row))} viewed the audit log</>;
    }
    if (a === 'sa.audit.exported') {
        return (
            <>
                {strong(actorLabel(row))} exported {strong(String(m.row_count ?? '?'))} audit row(s) as {m.format ?? 'csv'}
                {m.capped && <> <span style={{ color: 'var(--sa-amber)' }}>(capped)</span></>}
            </>
        );
    }

    // ── Fallback ────────────────────────────────────────────────────────
    return (
        <>
            <span className="sa-mono" style={{ fontSize: 'var(--sa-fs-xs)' }}>{a}</span>
            {row.target_type && <> on {row.target_type} #{row.target_id}</>}
        </>
    );
}

/**
 * Severity bucket drives the color pill in the list. Only three values so
 * the eye can learn them: danger (red) = attack surface or errors,
 * notice (amber) = lifecycle changes worth watching, info (dimmed) =
 * routine or pure reads.
 */
export type Severity = 'danger' | 'notice' | 'info';

export function severityFor(action: string): Severity {
    if (action === 'sa.login.failed') return 'danger';
    if (action === 'sa.impersonation.write_blocked') return 'danger';
    if (action === 'sa.totp.reset_cli') return 'danger';
    if (action === 'sa.impersonation.force_ended') return 'danger';

    if (action.startsWith('sa.company.')) return 'notice';
    if (action.startsWith('sa.impersonation.')) return 'notice';
    if (action.startsWith('sa.totp.')) return 'notice';
    if (action === 'sa.user.password_reset') return 'notice';
    if (action === 'sa.user.created') return 'notice';

    // login.success, logout, audit.viewed/exported, archive_reminders, etc.
    return 'info';
}
